import { useMemo } from 'react';
import { getEngine } from '@lio/rule-engine';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { RuleInput, StructuredQuote } from '@store/Quote/Quote.types';
import { DataType } from '@services/data/productModelTypes';
import { shallow } from 'zustand/shallow';

export function fieldHasNoValue(value: unknown, dataType: string | null | undefined): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (dataType === DataType.StringList && Array.isArray(value) && value.length === 0)
  );
}

export function getEffectiveValue(attributeType: { attribute?: { value?: unknown; valueOverride?: unknown } | null }) {
  return attributeType.attribute?.valueOverride ?? attributeType.attribute?.value;
}

export function hasMissingRequiredBeforeBindFields(
  structuredQuote: StructuredQuote,
  ruleEngine: ReturnType<typeof getEngine>,
): boolean {
  return structuredQuote.quoteItems.some((quoteItem) => {
    ruleEngine.setEngineInitialQuoteItem(quoteItem.id);

    if (!ruleEngine.execute(quoteItem.quoteItemType.isActive as RuleInput)) {
      return false;
    }

    return Object.values(quoteItem.attributeTypes ?? {}).some((attributeType) => {
      if (
        !ruleEngine.execute(attributeType.isActive as RuleInput) ||
        !ruleEngine.execute((attributeType.isRequiredBeforeBind ?? null) as RuleInput)
      ) {
        return false;
      }

      const value = getEffectiveValue(attributeType);
      return fieldHasNoValue(value, attributeType.dataType);
    });
  });
}

export function useHasMissingRequiredBeforeBindFields(options?: { skip?: boolean }): boolean {
  const { structuredQuote, ruleEngine } = useQuoteStore(
    (state) => ({ structuredQuote: state.structuredQuote, ruleEngine: state.ruleEngine }),
    shallow,
  );

  return useMemo(() => {
    if (options?.skip) return false;
    if (!structuredQuote || !ruleEngine) return false;
    return hasMissingRequiredBeforeBindFields(
      structuredQuote as StructuredQuote,
      ruleEngine as ReturnType<typeof getEngine>,
    );
  }, [structuredQuote, ruleEngine, options?.skip]);
}
